import { AnalyticsReportingDisabledKey, getSaninitizedClientID, logAnalyticsEvent } from '@vestico/utils';

import { BaseConfigurations, Configurations, ConversionConfigurations } from './model/api';
import { recordConversion } from './services/conversion';
import { getIsDeveloperPreview, getIsFineTuneEnabled, getIsInternal } from './services/helpers';

type MethodNames = 'init' | 'event' | 'conversion' | 'storeOpts';
export const DEFAULT_NAME = '_hw';

export interface VesticoWindow extends Window {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
}
/**
 * Represents a model that is created in embedded script
 * as part of script initialization.
 */
interface LoaderObject {
  /**
   * Queue that accumulates method calls during downloading
   * and loading of widget's script file.
   */
  q: Array<[MethodNames, BaseConfigurations]>;
}

/**
 * Loads widget instance.
 *
 * @param win Global window object which stores pre-loaded and post-loaded state of widget instance.
 * @param defaultConfig A configurations that are merged with user.
 * @param scriptElement The script tag that includes installation script and triggered loader.
 * @param render A method to be called once initialization done and DOM element for hosting widget is ready.
 */
export default (
  win: VesticoWindow,
  defaultConfig: Configurations,
  scriptElement: Element | null,
  renderPopup: (element: HTMLElement, config: Configurations) => void
) => {
  // this will an root element of the widget instance
  let targetElement: HTMLElement | undefined;

  const instanceName = scriptElement?.attributes.getNamedItem('id')?.value ?? DEFAULT_NAME;

  const initWidget = (config: BaseConfigurations) => {
    const loadedObject = Object.assign(defaultConfig, config);

    const alreadyLoaded = !!targetElement;

    console.info(`Starting widget [${instanceName}]`, loadedObject);

    // Fix client id to conform to sendable format
    loadedObject.clientId = getSaninitizedClientID(loadedObject.clientId);

    // the actual rendering of the widget
    if (!alreadyLoaded) {
      targetElement = win.document.createElement('div');
      targetElement.setAttribute('id', `vw-${instanceName}`);
      targetElement.setAttribute('style', 'position:fixed;z-index:2147483647;');
    }

    if (targetElement) {
      renderPopup(targetElement, loadedObject);
    }

    if (!alreadyLoaded) {
      // <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap" rel="stylesheet">
      const fontElement = win.document.createElement('link');
      fontElement.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap';
      fontElement.setAttribute('vw-styles', '');
      fontElement.rel = 'stylesheet';
      document.head.appendChild(fontElement);

      // store indication that widget instance was initialized
      win[`loaded-${instanceName}`] = true;
      win[`config-${instanceName}`] = loadedObject;
    }
  };

  const trackConversion = (trackingInfo: ConversionConfigurations) => {
    trackingInfo.clientId = getSaninitizedClientID(trackingInfo.clientId);
    recordConversion(trackingInfo).catch((e) => logAnalyticsEvent({ category: 'exception', event: 'record-conversion', name: e.message }));
  };

  const storeOptions = () => {
    console.info('Just storing options');
    getIsFineTuneEnabled();
    getIsDeveloperPreview();
    getIsInternal();
  };

  // get a hold of script tag instance, which has an
  // attribute `id` with unique identifier of the widget instance
  const loaderObject: LoaderObject = win[instanceName] as LoaderObject;

  if (!loaderObject || !loaderObject.q) {
    throw new Error(
      `Widget didn't find LoaderObject for instance [${instanceName}].\n\
      The loading script was either modified, no call to 'init' method was done\n\
      or there is conflicting object defined in \`window.${instanceName}\` .`
    );
  }

  // check that the widget is not loaded twice under the same name
  if (win[`loaded-${instanceName}`]) {
    throw new Error(
      `Widget with name [${instanceName}] was already loaded. ` +
        `This means you have multiple instances with same identifier (e.g. '${DEFAULT_NAME}')`
    );
  }

  // iterate over all methods that were called up until now
  for (let i = 0; i < loaderObject.q.length; i++) {
    const item = loaderObject.q[i];
    const methodName = item[0];
    if (i === 0 && !(methodName === 'init' || methodName === 'conversion' || methodName === 'storeOpts')) {
      throw new Error(`Failed to start Widget [${instanceName}]. 'init' must be called before other methods.`);
    } else if (i !== 0 && methodName === 'init') {
      continue;
    }

    const config = item[1] as BaseConfigurations;
    win[AnalyticsReportingDisabledKey] = config.reportingDisabled;

    switch (methodName) {
      case 'init':
        initWidget(config);
        break;
      case 'conversion':
        trackConversion(config as ConversionConfigurations);
        break;
      case 'storeOpts':
        storeOptions();
        break;
      default:
        console.warn(`Unsupported async method [${methodName}]`, config);
    }
  }

  // once finished processing all async calls, we going
  // to convert LoaderObject into sync calls to methods
  win[instanceName] = (method: MethodNames, ...args: unknown[]) => {
    switch (method) {
      // here you can handle additional sync interactions
      // with the widget from page
      case 'event': {
        targetElement?.dispatchEvent(new CustomEvent('vw-event', { detail: { name: args?.[0], callingElement: args?.[1] } }));
        break;
      }
      case 'conversion': {
        const trackingInfo = args?.[0] as ConversionConfigurations;
        trackConversion(trackingInfo);
        break;
      }
      case 'init': {
        const config = args?.[0] as BaseConfigurations;
        initWidget(config);
        break;
      }
      case 'storeOpts': {
        storeOptions();
        break;
      }
      default:
        // skipcq JS-0378
        console.warn(`Unsupported sync method [${method}]`, args);
    }
  };
};
